import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import {
  FormResponseCreateRequest,
  FormResponseUpdateRequest,
  FormResponseDetail,
  FormResponseListResponse,
} from '../models';
import { environment } from '@fitness-force/environments';

@Injectable({
  providedIn: 'root',
})
export class FormResponseService {
  private readonly http = inject(HttpClient);
  private readonly API_BASE_URL = environment.BASE_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  createResponse(
    tenantId: string,
    payload: FormResponseCreateRequest,
  ): Observable<{
    id: string;
    uuid: string;
    shareable_url: string | null;
    status: string | null;
    expiry_date: string;
  }> {
    return this.http.post<{
      id: string;
      uuid: string;
      shareable_url: string | null;
      status: string | null;
      expiry_date: string;
    }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.CREATE_FORM_RESPONSE.replace(
          '{tenant_id}',
          tenantId,
        ),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: unknown) =>
        throwError(() => new AppErrors(error))
      )
    );
  }

  updateResponse(
    tenantId: string,
    responseId: string,
    payload: FormResponseUpdateRequest,
  ): Observable<{ id: string; message: string }> {
    return this.http.put<{ id: string; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.UPDATE_FORM_RESPONSE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{response_id}', responseId),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: unknown) =>
        throwError(() => new AppErrors(error))
      )
    );
  }

  updateResponseDraft(
    tenantId: string,
    responseId: string,
    payload: { responseJson: string },
  ): Observable<{ id: string; message: string }> {
    return this.http.post<{ id: string; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.SAVE_DRAFT_FORM_RESPONSE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{response_id}', responseId),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: unknown) =>
        throwError(() => new AppErrors(error))
      )
    );
  }

  updateResponseComplete(
    tenantId: string,
    responseId: string,
    payload: { responseJson: string },
  ): Observable<{ id: string; message: string }> {
    return this.http.post<{ id: string; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.COMPLETE_FORM_RESPONSE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{response_id}', responseId),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: unknown) =>
        throwError(() => new AppErrors(error))
      )
    );
  }

  getMemberResponses(
    tenantId: string,
    clientId: string,
  ): Observable<FormResponseListResponse> {
    return this.http.get<FormResponseListResponse>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.GET_MEMBER_FORM_RESPONSES.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{client_id}', clientId),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Member Form Responses API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  deleteResponse(
    tenantId: string,
    responseId: string,
  ): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.DELETE_FORM_RESPONSE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{response_id}', responseId),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Delete Form Response API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getFormResponseContext(
    tenantId: string,
    responseId: string,
  ): Observable<FormResponseDetail> {
    return this.http.get<FormResponseDetail>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.GET_FORM_RESPONSE_CONTEXT.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{response_id}', responseId),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Form Response Context API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Public API endpoints for member-facing forms
  getPublicFormResponse(
    companyUuid: string,
    tenantUuid: string,
    responseUuid: string,
  ): Observable<FormResponseDetail> {
    return this.http.get<FormResponseDetail>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.GET_PUBLIC_FORM_RESPONSE.replace(
          '{company_uuid}',
          companyUuid,
        )
          .replace('{tenant_uuid}', tenantUuid)
          .replace('{response_uuid}', responseUuid),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Public Form Response API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  saveDraft(
    companyUuid: string,
    tenantUuid: string,
    responseUuid: string,
    payload: { responseJson: unknown },
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.SAVE_FORM_DRAFT.replace(
          '{company_uuid}',
          companyUuid,
        )
          .replace('{tenant_uuid}', tenantUuid)
          .replace('{response_uuid}', responseUuid),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Save Form Draft API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  submitComplete(
    companyUuid: string,
    tenantUuid: string,
    responseUuid: string,
    payload: { responseJson: unknown },
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.SUBMIT_FORM_COMPLETE.replace(
          '{company_uuid}',
          companyUuid,
        )
          .replace('{tenant_uuid}', tenantUuid)
          .replace('{response_uuid}', responseUuid),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Submit Form Complete API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
